import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Slider from '@material-ui/core/Slider';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import StopIcon from '@material-ui/icons/Stop';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import useQlikApp from 'hooks/useQlikApp';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import data from '../../data';

const useStyles = makeStyles(() => ({
  slider: (props) => ({
    marginTop: '10px',
    paddingBottom: '155px',
    '& .MuiSlider-markLabel': {
      transform: 'translateX(-90%) rotate(190deg)',
      writingMode: 'vertical-rl',
    },
    '& .MuiSlider-mark': {
      width: '10px !important',
      height: '10px !important',
      marginTop: '-5px !important',
      marginLeft: '-5px !important',
      border: '1px solid #404040 !important',
      position: 'absolute',
      borderRadius: '50% !important',
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF !important',
      opacity: '1 !important',
    },
    '& .MuiSlider-thumb': {
      width: '10px !important',
      height: '10px !important',
      marginTop: '-5px !important',
      marginLeft: '-5px !important',
      backgroundColor: '#009845 !important',
      border: '1px solid #009845 !important',
    },
    '& .MuiSlider-rail': {
      height: '1px !important',
    },
    '& .MuiSlider-track': {
      height: '1px !important',
      backgroundColor: '#404040 !important',
    },
    '.MuiCheckbox-colorSecondary.Mui-checked': {
      color: 'rgba(0, 0, 0, 0.54)',
    },
    ...props,
  }),
  button: {
    border: '1px solid #D4D7C6',
    borderRadius: '3px !important',
    marginLeft: '10px',
    marginRight: '10px',
    boxShadow: 'none !important',
    textTransform: 'capitalize',
  },
  buttonArrow: {
    border: '1px solid #D4D7C6',
    padding: '8px !important',
    minWidth: '28px !important',
    borderRadius: '100% !important',
    boxShadow: 'none !important',
    '& .MuiButton-endIcon': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  buttonPrev: {
    '& .MuiButton-label': {
      position: 'relative',
      left: 3,
    },
  },
}));

const SliderUI = ({ countryMedalsState, lang }) => {
  const qlikApp = useQlikApp();
  const intervalIdRef = useRef(0);

  const [isRunning, setIsRunning] = useState(false);
  const [marksCSS, setMarksCSS] = useState({});
  const [currentYearState, setCurrentYearState] = useState(27);
  const [stylePropsState, setStylePropsState] = useState({});

  const qFilter = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qListObjectDef: {
        qDef: {
          qFieldDefs: ['[YearCity]'],
          qFieldLabels: ['YearCity'],
        },
        qFrequencyMode: 'V',
      },
    },
  });

  const yearOptions = useMemo(() => {
    if (!qFilter.layout) return [];
    return qFilter.layout.qListObject.qDataPages[0].qMatrix.map((row, i) => ({
      qElemNumber: row[0].qElemNumber,
      qState: row[0].qState,
      qText: row[0].qText,
      value: i * (100 / (qFilter.layout.qListObject.qDataPages[0].qMatrix.length - 1)),
      label: row[0].qText,
    }));
  }, [qFilter.layout]);

  const selectedYear = useMemo(() => {
    if (!qFilter.layout) return [];
    return qFilter.layout.qListObject.qDataPages[0].qMatrix
      .filter((row) => row[0].qState === 'S')
      .map((row) => ({ year: row[0].qElemNumber, yearName: row[0].qText }));
  }, [qFilter.layout]);

  const changeYear = useCallback((event, newValue) => {
    if (!qFilter.layout) return;
    const currentYear = Math.round(newValue / (100 / (yearOptions.length - 1)));
    setCurrentYearState(currentYear);
    qFilter.model.selectListObjectValues('/qListObjectDef', [currentYear], false);
  }, [qFilter.layout]);

  const handleBack = () => {
    if (!qFilter.layout || !selectedYear.length) return;
    qFilter.model.selectListObjectValues('/qListObjectDef', [selectedYear[0].year - 1], false);
  };

  const handleNext = () => {
    if (!qFilter.layout || !selectedYear.length) return;
    qFilter.model.selectListObjectValues('/qListObjectDef', [selectedYear[0].year + 1], false);
  };

  const handleAnimate = () => {
    setIsRunning(!isRunning);
  };

  useEffect(() => {
    if (isRunning && yearOptions) {
      intervalIdRef.current = setInterval(() => {
        const options = yearOptions.filter((option) => option.qState !== 'X');
        const selectedIndex = options.findIndex((option) => option.qState === 'S');
        const nextElem = selectedIndex + 1 <= options.length - 1 ? options[selectedIndex + 1].qElemNumber : options[0].qElemNumber;
        qFilter.model.selectListObjectValues('/qListObjectDef', [nextElem], false);
      }, 1000);
    }
    return () => clearInterval(intervalIdRef.current);
  }, [isRunning, yearOptions]);

  useEffect(() => {
    if (!currentYearState) return;
    const activeYearArray = countryMedalsState.filter((d) => d.year === currentYearState);
    const arrayToObject = (array, type) => array.reduce((obj, item) => {
      obj[`${type === 'active' ? '& .MuiSlider-thumb' : `& .MuiSlider-mark[data-index="${item.year}"]`}`] = {
        width: `${item.medalsScaled}px !important`,
        height: `${item.medalsScaled}px !important`,
        marginTop: `-${item.medalsScaled / 2}px !important`,
        marginLeft: `-${item.medalsScaled / 2}px !important`,
      };
      return obj;
    }, {});

    if (countryMedalsState.length > 0) {
      setMarksCSS({
        ...arrayToObject(countryMedalsState, ''),
        ...arrayToObject(activeYearArray, 'active'),
      });
    } else {
      const _obj = {};
      for (let i = 0; i <= 27; i += 1) {
        _obj[`& .MuiSlider-mark[data-index="${i}"]`] = {
          width: '10px !important',
          height: '10px !important',
          marginTop: '-5px !important',
          marginLeft: '-5px !important',
        };
      }
      setMarksCSS({
        ..._obj,
        '& .MuiSlider-thumb': {
          width: '10px !important',
          height: '10px !important',
          marginTop: '-5px !important',
          marginLeft: '-5px !important',
        },
      });
    }
  }, [countryMedalsState, currentYearState]);

  useEffect(() => {
    setStylePropsState(marksCSS);
  }, [marksCSS]);

  const classes = useStyles(stylePropsState);

  return (
    <div>
      <Box className={classes.sliderControls}>
        <Button
          variant="contained"
          color="secondary"
          className={clsx([classes.buttonArrow, classes.buttonPrev])}
          onClick={handleBack}
          disabled={!!(selectedYear.length && (selectedYear[0].year === 0))}
          endIcon={<ArrowBackIosIcon />}
        />
        <Button
          variant="contained"
          color="secondary"
          className={classes.button}
          onClick={handleAnimate}
          endIcon={isRunning ? <StopIcon /> : <PlayArrowIcon />}
        >
          {lang === 'jp' ? data.jp.filters.animate : data.en.filters.animate}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          className={classes.buttonArrow}
          onClick={handleNext}
          disabled={!!(selectedYear.length && (selectedYear[0].year === 27))}
          endIcon={<ArrowForwardIosIcon />}
        />
      </Box>

      <Slider
        defaultValue={100}
        value={selectedYear.length && (selectedYear[0].year * (100 / (yearOptions.length - 1)))}
        step={100 / (yearOptions.length - 1)}
        marks={yearOptions}
        onChange={changeYear}
        className={classes.slider}
      />
    </div>
  );
};

SliderUI.propTypes = {
  countryMedalsState: PropTypes.array.isRequired,
  lang: PropTypes.string,
};

SliderUI.defaultProps = {
  lang: 'en',
};

export default SliderUI;
